import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { boolFromUnknown } from '@/lib/primitive-utils';
import type { Editor } from '@tiptap/core';
import { type ReactNodeViewProps, useEditorState } from '@tiptap/react';
import katex from 'katex';
import { ChevronDown, ChevronRight, XIcon } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { replaceEngineeringSymbolCommands, startOfUnitsRegex } from 'zycommon-web/utils/engineering-symbols';
import { ShortAnswerMathliveAnswerField } from './mathlive/short-answer-mathlive-answer-field';
import {
    attrsToBuilderProps,
    type BuilderPropertyRecord,
    DEFAULT_SHORT_ANSWER_TOOL_LABEL,
    isPlainTextAnswerTool,
    mergeBuilderPatchIntoAttrs,
    SHORT_ANSWER_TOOL_LABELS,
} from './tool-settings';
import { ShortAnswerToolSettingsWrapper } from './tool-settings/short-answer-tool-settings-wrapper';

const ShortAnswerAnswerNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, getPos, editor, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const builderProps = attrsToBuilderProps(node.attrs);
    const resolvedToolLabel = node.attrs.toolLabel ?? DEFAULT_SHORT_ANSWER_TOOL_LABEL;
    const usePlainTextAnswer = isPlainTextAnswerTool(resolvedToolLabel);
    const giveUnits = boolFromUnknown(builderProps.give_units);
    const showUnitPreview = !usePlainTextAnswer && giveUnits;
    const answers: string[] = Array.isArray(node.attrs.answers) && node.attrs.answers.length > 0 ? node.attrs.answers : [''];

    const hasAnswerWrapper = useEditorState({
        editor,
        selector: ({ editor: ed }) => (ed ? shortAnswerParentHasAnswerWrapper(ed, getPos) : false),
    });

    const patchAttrs = useCallback(
        (patch: BuilderPropertyRecord) => {
            updateAttributes(mergeBuilderPatchIntoAttrs(node.attrs as Parameters<typeof mergeBuilderPatchIntoAttrs>[0], patch));
        },
        [node, updateAttributes],
    );

    const handleAnswerChange = (index: number, value: string) => {
        const next = [...answers];
        next[index] = value;
        updateAttributes({
            answers: next,
        });
    };

    const handleAddAnswer = () => {
        updateAttributes({ answers: [...answers, ''] });
    };

    const handleAddAnswerWrapper = () => {
        if (shortAnswerParentHasAnswerWrapper(editor, getPos)) {
            return;
        }
        const pos = getPos();
        if (typeof pos !== 'number') {
            console.error('shortAnswerAnswer pos not found.');
            return;
        }
        const $pos = editor.state.doc.resolve(pos);
        editor.commands.insertShortAnswerAnswerWrapper($pos.parent);
    };

    const handleRemoveAnswer = (index: number) => {
        if (answers.length <= 1) return;
        if (!window.confirm('Are you sure you want to delete this answer?')) return;
        const effIdx = getEffectivePreferredIndex(answers, node.attrs.preferredIndex);
        const newAnswers = answers.filter((_, i) => i !== index);
        let newPreferredIndex: number | null = typeof node.attrs.preferredIndex === 'number' ? node.attrs.preferredIndex : null;

        if (effIdx === index) {
            newPreferredIndex = null;
        } else if (effIdx != null && effIdx > index) {
            newPreferredIndex = effIdx - 1;
        }

        updateAttributes({ answers: newAnswers, preferredIndex: newPreferredIndex });
    };

    const handlePreferredChange = (index: number) => {
        const effIdx = getEffectivePreferredIndex(answers, node.attrs.preferredIndex);
        const togglingOff = effIdx === index;
        updateAttributes({
            preferredIndex: togglingOff ? null : index,
        });
    };

    const handleSelectTool = (toolLabel: string) => {
        const pos = getPos();
        if (typeof pos !== 'number') {
            return;
        }
        editor.chain().applyShortAnswerTool(pos, toolLabel).run();
    };

    return (
        <ZyNodeViewWrapper
            decorations={decorations}
            className="mt-4 w-full rounded-lg border-2 border-dashed p-3"
        >
            <div
                className="space-y-3 border-gray-200 pb-3 dark:border-gray-700"
                contentEditable={false}
            >
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="flex min-w-0 items-center gap-2 text-left text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
                    >
                        <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                        Advanced tool settings
                    </button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                type="button"
                                data-style="ghost"
                                className="h-8 max-w-72 min-w-0 shrink-0 justify-between gap-1 border border-gray-300 px-2 text-sm font-normal dark:border-gray-600"
                                aria-label={`Assessment tool: ${resolvedToolLabel}. Change tool.`}
                                disabled={isEditorReadonly}
                                tooltip={`Tool: ${resolvedToolLabel}`}
                            >
                                <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                                    <span
                                        className="min-w-0 truncate font-medium text-gray-700 dark:text-gray-200"
                                        title={resolvedToolLabel}
                                    >
                                        {resolvedToolLabel}
                                    </span>
                                </span>
                                <ChevronDown
                                    className="size-4 shrink-0 opacity-70"
                                    aria-hidden
                                />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="max-h-80 overflow-y-auto"
                            align="end"
                        >
                            <DropdownMenuRadioGroup
                                value={resolvedToolLabel}
                                onValueChange={handleSelectTool}
                            >
                                {SHORT_ANSWER_TOOL_LABELS.map(label => (
                                    <DropdownMenuRadioItem
                                        key={label}
                                        value={label}
                                    >
                                        {label}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                {showAdvanced ? (
                    <div className="space-y-3 rounded border border-gray-200 bg-gray-100 p-3 dark:border-gray-700 dark:bg-gray-900/50">
                        <ShortAnswerToolSettingsWrapper
                            key={`${node.attrs.id}-${resolvedToolLabel}`}
                            toolLabel={resolvedToolLabel}
                            builderProps={builderProps}
                            onPatch={patchAttrs}
                            disabled={isEditorReadonly}
                        />
                    </div>
                ) : null}
            </div>
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-400">Answer{answers.length > 1 ? 's' : ''}:</span>
                </div>
                <div className="flex gap-x-2">
                    <div contentEditable={false}>
                        <Button
                            type="button"
                            onClick={handleAddAnswer}
                            disabled={!usePlainTextAnswer || isEditorReadonly}
                            data-size="small"
                            aria-label="Add answer"
                            tooltip="Add answer option"
                        >
                            + Add Answer
                        </Button>
                    </div>
                    <div contentEditable={false}>
                        <Button
                            type="button"
                            onClick={handleAddAnswerWrapper}
                            data-size="small"
                            aria-label="Add answer wrapper"
                            disabled={!usePlainTextAnswer || hasAnswerWrapper || isEditorReadonly}
                            tooltip={hasAnswerWrapper ? 'One wrapper allowed per question.' : 'Add answer wrapper'}
                        >
                            + Add Wrapper
                        </Button>
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                {answers.length > 1 && (
                    <div className="mb-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <span>Preferred answer:</span>
                        {getEffectivePreferredIndex(answers, node.attrs.preferredIndex) != null && (
                            <button
                                type="button"
                                onClick={() => {
                                    updateAttributes({ preferredIndex: null });
                                }}
                                disabled={isEditorReadonly}
                                className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                                contentEditable={false}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}
                {answers.map((answer, index) => {
                    const isBlank = answer.trim() === '';
                    const errorId = `short-answer-blank-${node.attrs.id}-${index}`;
                    return (
                        <div key={`${node.attrs.id}-${index}`}>
                            <div className="flex items-center gap-2">
                                {answers.length > 1 && (
                                    <input
                                        type="radio"
                                        name={`preferred-answer-${node.attrs.id}`}
                                        checked={getEffectivePreferredIndex(answers, node.attrs.preferredIndex) === index}
                                        onChange={() => handlePreferredChange(index)}
                                        disabled={isEditorReadonly}
                                        title="Mark as preferred answer"
                                        className="h-4 w-4 cursor-pointer text-blue-600"
                                        contentEditable={false}
                                    />
                                )}
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    {usePlainTextAnswer ? (
                                        <Input
                                            type="text"
                                            key={`${node.attrs.id}-${index}-input`}
                                            value={answer}
                                            onChange={e => handleAnswerChange(index, e.target.value)}
                                            readOnly={isEditorReadonly}
                                            placeholder={`Answer ${index + 1}`}
                                            aria-invalid={isBlank}
                                            aria-describedby={isBlank ? errorId : undefined}
                                        />
                                    ) : (
                                        <ShortAnswerMathliveAnswerField
                                            key={`${node.attrs.id}-${index}-mathlive`}
                                            value={answer}
                                            onChange={v => handleAnswerChange(index, v)}
                                            readOnly={isEditorReadonly}
                                            toolLabel={resolvedToolLabel}
                                            builderProps={builderProps}
                                            placeholder={`Answer ${index + 1}`}
                                        />
                                    )}
                                </div>
                                {showUnitPreview ? (
                                    <AnswerUnitPreview
                                        answer={answer}
                                        builderProps={builderProps}
                                    />
                                ) : null}
                                <div contentEditable={false}>
                                    <Button
                                        type="button"
                                        onClick={() => handleRemoveAnswer(index)}
                                        data-style="ghost"
                                        aria-label="Remove answer"
                                        disabled={answers.length <= 1 || isEditorReadonly}
                                        tooltip={answers.length <= 1 ? 'At least one answer is required' : 'Remove answer'}
                                    >
                                        <XIcon className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <span
                                id={errorId}
                                className={`text-xs text-red-600 dark:text-red-400${!isBlank ? ' hidden' : ''} ${answers.length > 1 ? 'ml-6' : ''}`}
                                aria-hidden={!isBlank}
                            >
                                Answer cannot be blank
                            </span>
                        </div>
                    );
                })}
            </div>
        </ZyNodeViewWrapper>
    );
};

function shortAnswerParentHasAnswerWrapper(editor: Editor, getPos: () => number | undefined): boolean {
    try {
        const pos = getPos();
        const docSize = editor.state?.doc?.content?.size;
        if (typeof pos !== 'number' || docSize == null || pos > docSize) {
            return false;
        }
        const $pos = editor.state.doc.resolve(pos);
        const parent = $pos.parent;
        if (parent.type.name !== 'shortAnswer') {
            return false;
        }
        let found = false;
        parent.forEach(child => {
            if (child.type.name === 'shortAnswerAnswerWrapper') {
                found = true;
                return;
            }
        });
        return found;
    } catch {
        return false;
    }
}

function getEffectivePreferredIndex(answers: string[], preferredIndex: number | null | undefined): number | null {
    if (typeof preferredIndex === 'number' && preferredIndex >= 0 && preferredIndex < answers.length) {
        return preferredIndex;
    }
    return null;
}

function parseMeasurementNumberAndUnitPair(answer: string): readonly [numberLatex: string, unitsLatex: string] {
    const result = answer.match(startOfUnitsRegex);

    if (result?.index != null) {
        const [number, units] = [answer.slice(0, result.index), answer.slice(result.index)].map(latex =>
            latex.replaceAll('\\;', '').replaceAll(' ', ''),
        );

        return [number, replaceEngineeringSymbolCommands(units)];
    }

    return ['', ''];
}

function AnswerUnitPreview({ answer }: { answer: string; builderProps: BuilderPropertyRecord }) {
    const [numberLatex, unitsLatex] = useMemo(() => parseMeasurementNumberAndUnitPair(answer), [answer]);
    const unitsHtml = useMemo(() => {
        if (unitsLatex === '') {
            return '';
        }

        return katex.renderToString(unitsLatex, {
            displayMode: false,
            throwOnError: false,
        });
    }, [unitsLatex]);

    return (
        <div className="flex shrink-0 items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            <span className="hidden sm:inline">Input preview:</span>
            <Input
                type="number"
                disabled
                value={numberLatex}
                readOnly
                aria-label="Number input preview"
                className="h-8 px-2 text-sm w-24"
            />
            {unitsHtml ? (
                <span
                    className="inline-flex items-center"
                    dangerouslySetInnerHTML={{ __html: unitsHtml }}
                />
            ) : (
                <span className="inline-flex items-center" />
            )}
        </div>
    );
}

export default memo(ShortAnswerAnswerNodeView);
